// src/components/TPRMRespondentsModal.js
// UN EOI Phase 2: manage multiple respondents on a TPRM assessment. Respondents are existing
// users (Contributor+) or new external people invited through the standard B2B invite flow
// (Contributor @ SELF scope via UserSelect's "+" button). One respondent is Primary — only
// they (or an internal manager) can submit the shared form.
import React, { useState, useEffect, useCallback } from "react";
import { X, Star, Trash2, AlertTriangle } from "lucide-react";
import { useApi } from "../hooks/useApi";
import UserSelect from "./UserSelect";

const STATUS_STYLES = {
    Pending: "bg-yellow-100 text-yellow-800",
    Accepted: "bg-blue-100 text-blue-800",
    Declined: "bg-red-100 text-red-700",
    Completed: "bg-green-100 text-green-800",
};

const TPRMRespondentsModal = ({ isOpen, onClose, assessmentId, refreshData }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const apiCall = useApi();

    const fetchAssignees = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiCall("GET", `/TPRMAssessments/${assessmentId}/assignees`);
            setData(res);
        } catch (err) {
            setError("Failed to load respondents.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assessmentId]);

    useEffect(() => {
        if (!isOpen) return;
        setError(null);
        setSelectedUserId("");
        fetchAssignees();
        (async () => {
            try {
                const res = await apiCall("GET", "/TPRMAssessments/assignableUsers");
                setUsers(res || []);
            } catch (err) {} finally {
                setLoadingUsers(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!selectedUserId) return;

        setIsProcessing(true);
        setError(null);
        try {
            const isInvite = selectedUserId.startsWith("invite:");
            const payload = isInvite
                ? { userInviteId: parseInt(selectedUserId.split(":")[1]) }
                : { userId: parseInt(selectedUserId) };
            await apiCall("POST", `/TPRMAssessments/${assessmentId}/assignees`, payload);
            setSelectedUserId("");
            await fetchAssignees();
            refreshData?.();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to add respondent.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRemove = async (assigneeId) => {
        setIsProcessing(true);
        setError(null);
        try {
            await apiCall("DELETE", `/TPRMAssessments/${assessmentId}/assignees/${assigneeId}`);
            await fetchAssignees();
            refreshData?.();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to remove respondent.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSetPrimary = async (assigneeId) => {
        setIsProcessing(true);
        setError(null);
        try {
            await apiCall("PUT", `/TPRMAssessments/${assessmentId}/assignees/${assigneeId}/primary`);
            await fetchAssignees();
            refreshData?.();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to set primary respondent.");
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isOpen) return null;

    const assignees = data?.assignees || [];

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border max-w-xl w-full shadow-lg rounded-md bg-white">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">
                        Assessment Respondents
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {error && (
                    <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {loading ? (
                    <p className="text-sm text-gray-500 py-4">Loading respondents...</p>
                ) : (
                    <div className="space-y-2 mb-4">
                        {data?.ownerUserId && (
                            <div className="flex items-center justify-between px-3 py-2 rounded-md border border-gray-200 bg-gray-50">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {data.ownerUserName || `User ${data.ownerUserId}`}
                                    </p>
                                    {data.ownerEmail && (
                                        <p className="text-xs text-gray-500 truncate">{data.ownerEmail}</p>
                                    )}
                                </div>
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 flex-shrink-0">
                                    Owner
                                </span>
                            </div>
                        )}

                        {assignees.length === 0 && (
                            <p className="text-sm text-gray-500 py-2">
                                No additional respondents yet. Add existing users or invite new ones below.
                            </p>
                        )}

                        {assignees.map((a) => (
                            <div key={a.id} className="flex items-center justify-between px-3 py-2 rounded-md border border-gray-200">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {a.displayName || a.email || (a.userInviteId ? `Invite #${a.userInviteId}` : `User ${a.userId}`)}
                                    </p>
                                    {a.email && a.displayName && (
                                        <p className="text-xs text-gray-500 truncate">{a.email}</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                    {a.inviteExpired && (
                                        <span className="flex items-center text-xs text-amber-700" title="Invitation expired — resend it from User Management">
                                            <AlertTriangle className="h-3.5 w-3.5 mr-0.5" />
                                            Expired
                                        </span>
                                    )}
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[a.invitationStatus] || "bg-gray-100 text-gray-700"}`}>
                                        {a.invitationStatus}
                                    </span>
                                    {a.isPrimary ? (
                                        <span className="flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800" title="Primary respondent — the one who submits the assessment">
                                            <Star className="h-3 w-3 mr-0.5 fill-current" />
                                            Primary
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled={isProcessing}
                                            onClick={() => handleSetPrimary(a.id)}
                                            className="text-xs text-gray-500 hover:text-cyan-700 underline disabled:opacity-50"
                                            title="Make this person the primary respondent"
                                        >
                                            Make primary
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        disabled={isProcessing}
                                        onClick={() => handleRemove(a.id)}
                                        className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                                        title="Remove respondent"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <form onSubmit={handleAdd} className="space-y-3 border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700">
                        Add Respondent
                    </label>
                    <UserSelect
                        value={selectedUserId}
                        onChange={setSelectedUserId}
                        users={users}
                        loading={loadingUsers}
                        onInviteSuccess={() => {}}
                    />
                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Close
                        </button>
                        <button
                            type="submit"
                            disabled={isProcessing || loadingUsers || !selectedUserId}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isProcessing ? "Processing..." : "Add"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TPRMRespondentsModal;
